import { useRef, useState, useEffect } from 'react'
import { useStaticQuery, graphql } from 'gatsby'
import startOfMonth from 'date-fns/startOfMonth'
import endOfMonth from 'date-fns/endOfMonth'
import format from 'date-fns/format'
import addWeeks from 'date-fns/addWeeks'
import subWeeks from 'date-fns/subWeeks'
import max from 'date-fns/max'
import useOnClickOutside from 'use-onclickoutside'
import BookingWidgetMobileDropdownMenu from './bookingWidgetMobileDropdownMenu'
import useStaylistAPIs from 'components/booking/context/useStaylistAPIs'
import { BodySemi } from 'components/common/typography'
import { ConditionalDiv } from 'components/common/common_styles'
import DatePicker from 'react-datepicker'
import { today } from 'utils/dateUtils'
import { MainContainer, CalendarPrice, DropdownRow, ApplyButton } from './bookingWidgetDateDropdown_styles'
import './bookingWidgetDatePicker.css'

const BookingWidgetDateDropdown = ({ close, startDate, setStartDate, endDate, setEndDate, $offset }) => {
  const { allPrismicSiteDetail } = useStaticQuery(query)
  const categories = allPrismicSiteDetail.edges.flatMap(edge => edge.node.data.staylist_unit_category_ids)
  const [currentMonthDate, setCurrentMonthDate] = useState(startDate ?? today())
  const [monthPrices, setMonthPrices] = useState([])
  const [currentCategoryId, setCurrentCategoryId] = useState(categories[0].id)
  const { getRatesList } = useStaylistAPIs()
  const ref = useRef()
  useOnClickOutside(ref, close)

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        close()
      }
    }
    document.addEventListener('keydown', onKeyDown, false)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [close])

  useEffect(() => {
    let ignore = false

    const getMonthPricing = async() => {
      const result = await getRatesList({
        start: format(max([subWeeks(startOfMonth(currentMonthDate), 1), today()]), 'yyyy-MM-dd'),
        end: format(addWeeks(endOfMonth(currentMonthDate), 1), 'yyyy-MM-dd'),
        category_id: currentCategoryId
      })
      if (!ignore) {
        setMonthPrices(result)
      }
    }

    getMonthPricing()

    return () => { ignore = true }
  }, [currentMonthDate, currentCategoryId, getRatesList])

  const onDayChange = dates => {
    const [start, end] = dates
    setStartDate(start)
    setEndDate(end)
  }

  const renderDayContents = (day, date) => {
    const curPrice = monthPrices?.find(price => price.date === format(date, 'yyyy-MM-dd'))?.rate

    return (
      <>
        <span aria-hidden>{day}</span>
        <CalendarPrice aria-hidden>{curPrice && curPrice > 0 ? `$${+curPrice}` : 'N/A'}</CalendarPrice>
      </>
    )
  }

  const onMonthChange = date => {
    setCurrentMonthDate(date)
  }

  const onCategoryChange = event => {
    setCurrentCategoryId(+event.target.value)
  }

  return (
    <MainContainer ref={ref} $offset={$offset}>
      <BookingWidgetMobileDropdownMenu title='Dates' onClose={close} />
      <DatePicker
        selected={startDate}
        onChange={onDayChange}
        startDate={startDate}
        endDate={endDate}
        minDate={today()}
        maxDate={!endDate ? addWeeks(startDate, 1) : null}
        onMonthChange={onMonthChange}
        renderDayContents={renderDayContents}
        selectsRange
        inline
      />
      <DropdownRow center gap={8}>
        <BodySemi as='label' htmlFor='priceselect'>Show prices for:</BodySemi>
        <select value={currentCategoryId} onChange={onCategoryChange} id='priceselect'>
          {categories.map(category =>
            <option key={category.id} value={category.id}>{category.name}</option>
          )}
        </select>
      </DropdownRow>
      <ConditionalDiv mobileOnly>
        <ApplyButton $black onClick={close} disabled={!startDate || !endDate}>APPLY</ApplyButton>
      </ConditionalDiv>
    </MainContainer>
  )
}

export default BookingWidgetDateDropdown

export const query = graphql`
  query {
    allPrismicSiteDetail(
      sort: {data: {order: ASC}}
      filter: {data: {active: {eq: true}}}
    ) {
      edges {
        node {
          data {
            staylist_unit_category_ids {
              id
              name
            }
          }
        }
      }
    }
  }
`
